import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { listTrackerPrivateNotesApi } from "src/DAL/tracker/tracker";
import {
  deletePrivateNoteApi,
  updateSubmissionPrivateNoteApi,
} from "src/DAL/tracker/trackerSubmission";
import { PrivateNotesList } from "./PrivateCoachNotesCard";
import { withBack } from "./trackerNav";

const MUTED = "#64748b";
const NOTE_MUTED = "#8a7b45";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

/**
 * The signed-in user's own private notes on this tracker's submissions
 * (notes are per author), grouped by submission id.
 */
export const useTrackerSubmissionNotes = (trackerId) => {
  const [notes, setNotes] = useState([]);

  const reload = useCallback(async () => {
    if (!trackerId) {
      setNotes([]);
      return;
    }
    try {
      const res = await listTrackerPrivateNotesApi(trackerId);
      if (res?.code === 200) setNotes(res.submissionNotes || []);
    } catch (_err) {
      // Non-critical — keep what is shown.
    }
  }, [trackerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const bySubmission = useMemo(() => {
    const map = new Map();
    notes.forEach((note) => {
      const id = String(note?.source?.submissionId || "");
      if (!id) return;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(note);
    });
    return map;
  }, [notes]);

  return { bySubmission, reload };
};

/** Notes of the given submissions (one submission, or all of one person's). */
export const notesForSubmissions = (bySubmission, submissionIds = []) =>
  submissionIds.flatMap((id) => bySubmission.get(String(id)) || []);

/** "📝 2" in a table cell; a dash when there are no notes. */
export const NotesCountButton = ({ count, onClick }) =>
  count ? (
    <Tooltip title="View your private notes">
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.9,
          py: 0.3,
          border: "1px solid #f1e2a4",
          borderRadius: "999px",
          bgcolor: "#fff8d6",
          color: NOTE_MUTED,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          "&:hover": { borderColor: "#e5cf74" },
        }}
      >
        <StickyNote2OutlinedIcon sx={{ fontSize: 15 }} />
        {count}
      </Box>
    </Tooltip>
  ) : (
    <Typography sx={{ fontSize: 13, color: MUTED }}>—</Typography>
  );

/**
 * Private notes of one submission or one person, newest first. Each note
 * links to its submission; edit / delete work here too.
 */
export default function SubmissionNotesDialog({
  open,
  onClose,
  title,
  notes = [],
  onChanged,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteNote, setDeleteNote] = useState(null);

  const sorted = useMemo(
    () =>
      [...notes]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .map((note) => ({
          ...note,
          key: `${note.source?.submissionId}-${note._id}`,
        })),
    [notes]
  );

  const handleEdit = async (note, text) => {
    try {
      const res = await updateSubmissionPrivateNoteApi(
        note.source.submissionId,
        note._id,
        text
      );
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not update the note", {
          variant: "error",
        });
        return false;
      }
      enqueueSnackbar("Note updated", { variant: "success" });
      await onChanged?.();
      return true;
    } catch (_err) {
      enqueueSnackbar("Could not update the note", { variant: "error" });
      return false;
    }
  };

  const handleDelete = async () => {
    const note = deleteNote;
    setDeleteNote(null);
    if (!note?._id) return;
    try {
      const res = await deletePrivateNoteApi(note.source.submissionId, note._id);
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not delete the note", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar("Note deleted", { variant: "success" });
      await onChanged?.();
    } catch (_err) {
      enqueueSnackbar("Could not delete the note", { variant: "error" });
    }
  };

  const getContext = (note) => (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/tracker-submissions/${note.source.submissionId}`, { state: withBack() })}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "100%",
        p: 0,
        border: 0,
        background: "none",
        cursor: "pointer",
        fontSize: 11.5,
        fontWeight: 700,
        color: NOTE_MUTED,
        textAlign: "left",
        "&:hover": { color: PRIMARY },
      }}
    >
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {[
          note.source.formName,
          note.source.submittedAt
            ? moment(note.source.submittedAt).format("D MMM YYYY")
            : "",
        ]
          .filter(Boolean)
          .join(" · ") || "Submission"}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 15, flexShrink: 0 }} />
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>
          {title || "Private notes"}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: 12.5, color: MUTED }}>
          Your private notes · only you can see them
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "4px !important" }}>
        {sorted.length ? (
          <PrivateNotesList
            layout="stack"
            notes={sorted}
            getContext={getContext}
            onEdit={handleEdit}
            onDelete={setDeleteNote}
          />
        ) : (
          <Typography sx={{ fontSize: 13, color: MUTED }}>No notes.</Typography>
        )}
      </DialogContent>

      <CustomConfirmation
        openDelete={Boolean(deleteNote)}
        setOpenDelete={(value) => {
          if (!value) setDeleteNote(null);
        }}
        title="Delete this note?"
        handleAgree={handleDelete}
      />
    </Dialog>
  );
}
